import React, { useState, useRef } from 'react';
import { PDFdownloadButton } from './Pdf';
import { VideoGateContext } from '../VideoGateContext';

const Quiz = ({
  moduleKey,
  title = 'End of Module Quiz',
  introContent = null,
  quizData,
  pdfSrc,
  pdfText,
  onSubmitQuiz,
}) => {
  const { registerQuizRequirement, markQuizPassed } =
    React.useContext(VideoGateContext);
  const [answers, setAnswers] = useState(
    quizData.map((question) =>
      question.selectionType === 'multi' ? [] : null,
    ),
  );
  const [submittedAnswers, setSubmittedAnswers] = useState(null); // frozen on submit
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const quizRef = useRef(null);

  React.useEffect(() => {
    registerQuizRequirement();
  }, [registerQuizRequirement]);

  const handleSelect = (qIndex, aIndex, option) => {
    const question = quizData[qIndex];
    const updated = [...answers];

    if (question.selectionType === 'multi') {
      const selectedAnswers = Array.isArray(updated[qIndex])
        ? [...updated[qIndex]]
        : [];
      const isSelected = selectedAnswers.includes(aIndex);
      const exclusiveOptionIndex = question.options.findIndex(
        (quizOption) => quizOption.exclusive,
      );

      if (option.exclusive) {
        updated[qIndex] = isSelected ? [] : [aIndex];
      } else {
        const filteredSelections = selectedAnswers.filter(
          (selectedIndex) => selectedIndex !== exclusiveOptionIndex,
        );

        updated[qIndex] = isSelected
          ? filteredSelections.filter((selectedIndex) => selectedIndex !== aIndex)
          : [...filteredSelections, aIndex].sort((first, second) => first - second);
      }
    } else {
      updated[qIndex] = aIndex;
    }

    setAnswers(updated);
  };

  const handleSubmit = () => {
    const correctAnswers = answers.filter(
      (answer, i) => {
        const question = quizData[i];

        if (question.selectionType === 'multi') {
          const selectedAnswers = Array.isArray(answer) ? answer : [];
          const correctAnswers = question.correctIndexes || [];

          return (
            selectedAnswers.length === correctAnswers.length &&
            selectedAnswers.every(
              (selectedAnswer, answerIndex) =>
                selectedAnswer === correctAnswers[answerIndex],
            )
          );
        }

        return answer === question.correctIndex;
      },
    ).length;
    const answerIds = answers.flatMap((answer, questionIndex) => {
      const question = quizData[questionIndex];

      if (question.selectionType === 'multi') {
        if (!Array.isArray(answer)) {
          return [];
        }

        return answer
          .map((selectedIndex) => question.options[selectedIndex]?.answerId)
          .filter((answerId) => Number.isInteger(answerId));
      }

      if (!Number.isInteger(answer)) {
        return [];
      }

      const answerId = question.options[answer]?.answerId;

      return Number.isInteger(answerId) ? [answerId] : [];
    });
    const passedQuiz = correctAnswers >= passingScore;
    const scorePercent =
      totalQuestions > 0
        ? Math.round((correctAnswers / totalQuestions) * 100)
        : 0;
    setScore(correctAnswers);
    setSubmittedAnswers([...answers]); // freeze the answers
    setSubmitted(true);

    if (passedQuiz) {
      markQuizPassed();
    }

    if (moduleKey && onSubmitQuiz) {
      onSubmitQuiz({
        module: Number(moduleKey),
        score: scorePercent,
        answerIds,
      });
    }

    // Scroll to the very top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    setSubmitted(false);
    setSubmittedAnswers(null);

    // Clear all answers and lock questions
    setAnswers(
      quizData.map((question) =>
        question.selectionType === 'multi' ? [] : null,
      ),
    );
  };

  const totalQuestions = quizData.length;
  const passingScore = 0.8 * totalQuestions;

  const optionLabels = ['A', 'B', 'C', 'D'];

  const perfectScore = score === totalQuestions && totalQuestions > 0;

  return (
    <div ref={quizRef} className='max-w-3xl mx-auto space-y-6 '>
      <h2 className='text-2xl font-bold'>{title}</h2>
      {introContent}
      <div
        className='flex flex-col justify-center bg-white p-6 rounded shadow-md mb-6 gap-2 
      border
      '
      >
        <p className='text-2xl font-bold mb-2'>To review the module content:</p>
        <PDFdownloadButton href={pdfSrc}>{pdfText}</PDFdownloadButton>
        {submitted && (
          <div className='text-center bg-gray-100 rounded p-4 mt-4 border-black border-2'>
            <p className={`text-2xl font-medium mb-4 `}>
              {' '}
              You Scored:{' '}
              <b
                className={`
                
                ${score >= passingScore ? 'text-green-600' : 'text-red-600'}`}
              >
                {score}/{totalQuestions}
              </b>
            </p>
            {score >= passingScore ? (
              <p className='font-bold'>
                🎉 Congratulations! You passed and can continue with the
                training!
              </p>
            ) : (
              <p className='text-black text-left'>
                To move on to the next module, trainees are required to achieve
                a score of <b>80%</b>, please review your answers below. If you
                would like to review content before you retake the quiz, click
                on the resource above.
              </p>
            )}
            {score < passingScore && submitted && (
              <button
                onClick={handleRetry}
                className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition mt-4'
                disabled={score >= passingScore}
              >
                Retry Quiz
              </button>
            )}
          </div>
        )}
      </div>
      {quizData.map((q, i) => {
        const submittedAnswer = submittedAnswers?.[i];
        const isCorrect = submitted && (() => {
          if (q.selectionType === 'multi') {
            const selectedAnswers = Array.isArray(submittedAnswer)
              ? submittedAnswer
              : [];
            const correctAnswers = q.correctIndexes || [];

            return (
              selectedAnswers.length === correctAnswers.length &&
              selectedAnswers.every(
                (selectedAnswer, answerIndex) =>
                  selectedAnswer === correctAnswers[answerIndex],
              )
            );
          }

          return submittedAnswer === q.correctIndex;
        })();
        const isWrong =
          submitted &&
          (q.selectionType === 'multi'
            ? !isCorrect
            : submittedAnswer === null || submittedAnswer !== q.correctIndex);

        return (
          <div
            key={i}
            className='border p-4 rounded bg-white shadow-sm text-left'
          >
            {q.header && (
              <h3 className='text-xl font-bold mb-2 text-gray-800'>
                {q.header}
              </h3>
            )}
            <p
              className={`mb-2 font-bold ${
                isWrong ? 'text-red-600' : 'text-dark-blue'
              }`}
            >
              {i + 1}. {q.question} {isCorrect && <span>✅</span>}
              {isWrong && <span>❌</span>}
            </p>
            <div className='space-y-2'>
              {q.options.map((option, j) => {
                const selectedAnswer = answers[i];
                const isSelected = q.selectionType === 'multi'
                  ? Array.isArray(selectedAnswer) && selectedAnswer.includes(j)
                  : selectedAnswer === j;
                const isSubmittedCorrect =
                  submitted &&
                  isSelected &&
                  (q.selectionType === 'multi'
                    ? (q.correctIndexes || []).includes(j)
                    : j === q.correctIndex);
                const isSubmittedWrong =
                  submitted &&
                  isSelected &&
                  (q.selectionType === 'multi'
                    ? !(q.correctIndexes || []).includes(j)
                    : j !== q.correctIndex);

                return (
                  <label
                    key={j}
                    className={`block px-3 py-2 rounded cursor-pointer border
                      ${isSelected ? 'border-blue-500' : 'border-gray-300'}
                      ${
                        isSubmittedCorrect
                          ? 'bg-green-100 border-green-500'
                          : isSubmittedWrong
                          ? 'bg-red-100 border-red-500'
                          : ''
                      }
                    `}
                  >
                    <input
                      type={q.selectionType === 'multi' ? 'checkbox' : 'radio'}
                      name={`question-${i}`}
                      value={j}
                      checked={isSelected}
                      onChange={() => handleSelect(i, j, option)}
                      disabled={submitted || submittedAnswers !== null} // Lock inputs on retry
                      className='mr-2'
                    />
                    <span className='font-bold mr-2'>{optionLabels[j]}.</span>
                    {option.text}
                  </label>
                );
              })}
            </div>
            {submitted &&
              ((q.selectionType === 'multi' &&
                Array.isArray(submittedAnswer) &&
                submittedAnswer.length > 0) ||
                (q.selectionType !== 'multi' && submittedAnswer !== null)) && (
              //if answer is correct make it greenbackground if not make it red
              <p
                className={` p-2 border-y-2 border-gray-300 mt-2 text-sm text-gray-700 font-bold ${
                  isCorrect ? 'bg-green-100 ' : isWrong ? 'bg-red-100 ' : ''
                } `}
              >
                {q.selectionType === 'multi'
                  ? q.feedback
                  : q.options[submittedAnswer]?.feedback}
              </p>
            )}
          </div>
        );
      })}
      {!submitted && (
        <button
          onClick={handleSubmit}
          className='bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition'
        >
          Submit
        </button>
      )}
    </div>
  );
};

export default Quiz;
